/**
 * Reconocimiento de habla del lado del cliente para la PRÁCTICA (M6). A
 * diferencia de la evaluación (que sube audio y lo procesa el pipeline ASR de
 * M2), la práctica solo necesita una secuencia fonémica aproximada para que el
 * umbral TOLERANTE del backend dé feedback motivador. Usamos la Web Speech API
 * cuando existe; si no, degradamos devolviendo null y el llamador decide.
 *
 * Nota: esto NO diagnostica (regla de oro: el motor tolerante solo motiva).
 */

interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  onresult: ((ev: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
}

function crearReconocedor(): SpeechRecognitionLike | null {
  const w = globalThis as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
}

export function soportaReconocimiento(): boolean {
  return crearReconocedor() !== null;
}

/** Escucha una vez y resuelve con el transcript (o null si no hubo/soporte). */
export function reconocerUnaVez(timeoutMs = 6000): Promise<string | null> {
  const rec = crearReconocedor();
  if (!rec) return Promise.resolve(null);
  rec.lang = "es-PE";
  rec.interimResults = false;
  rec.maxAlternatives = 1;
  return new Promise<string | null>((resolve) => {
    let resuelto = false;
    const finalizar = (val: string | null) => {
      if (resuelto) return;
      resuelto = true;
      resolve(val);
    };
    rec.onresult = (ev) => {
      const t = ev.results?.[0]?.[0]?.transcript ?? "";
      finalizar(t.trim() || null);
    };
    rec.onerror = () => finalizar(null);
    rec.onend = () => finalizar(null);
    window.setTimeout(() => {
      try {
        rec.stop();
      } catch {
        /* noop */
      }
      finalizar(null);
    }, timeoutMs);
    try {
      rec.start();
    } catch {
      finalizar(null);
    }
  });
}

/** Convierte texto a una secuencia fonémica placeholder (grafema→"fonema"),
 *  coherente con eval/stimuli.ts. */
export function textoAFonemas(texto: string): string[] {
  return texto
    .toLowerCase()
    .replace(/\//g, "")
    .split("")
    .filter((c) => /[a-záéíóúñü]/i.test(c));
}
